import { useState, useCallback } from "react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import {
  optimizeImageForPDF,
  detectOptimalFormat,
} from "../utils/imageOptimizer";
import { getPresignedUrl, uploadFileToS3, createSurvey } from "@/api/surveys";
import type { FormContextValue } from "../contexts/FormContext";
import type { CalibrationContextValue } from "../contexts/CalibrationContext";
import type { ShedConfigContextValue } from "../contexts/ShedConfigContext";
import type { ProfileResponse } from "@/api/profile";

export interface GeneratePdfParams {
  orderId?: string;
  form: FormContextValue;
  calibration: CalibrationContextValue;
  shedConfig: ShedConfigContextValue;
  profile?: ProfileResponse | null;
}

function buildConfiguration(
  form: FormContextValue,
  calibration: CalibrationContextValue,
  shedConfig: ShedConfigContextValue,
): Record<string, unknown> {
  return {
    name: form.name,
    email: form.email,
    phone: form.phone,
    address: form.address,
    has_wind_speed: form.hasWindSpeed,
    wind_speed: form.windSpeed,
    is_anchored: form.isAnchored,
    materials_text: form.materialsText,
    material_type: form.materialType,
    metal_color: form.metalColor,
    shingle_type: form.shingleType,
    shingle_color: form.shingleColor,
    shingle_color_other: form.shingleColorOther,
    shingle_brand: form.shingleBrand,
    shingle_brand_other: form.shingleBrandOther,
    shed_style: shedConfig.shedStyle,
    is_attached: shedConfig.isAttached,
    has_blocks: shedConfig.hasBlocks,
    dimensions: shedConfig.dimensions,
    doors: shedConfig.doors,
    extras: shedConfig.extras,
    calib_points: calibration.calibPoints,
    scale_factor: calibration.scaleFactor,
    shed_position: calibration.shedPosition,
    shed_rotation: calibration.shedRotation,
  };
}

export function usePdfGeneration() {
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePdf = useCallback(
    async ({ form, calibration, shedConfig, profile }: GeneratePdfParams) => {
      const isBuilder = profile?.role === "builder";
      setIsGenerating(true);

      try {
        const pdf = new jsPDF({
          orientation: "p",
          unit: "mm",
          format: "letter",
          compress: true,
          precision: 2, // Reduce precision for smaller file size
          userUnit: 1.0, // Use default unit scaling
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 15;
        let yPosition = margin;

        const addHeader = async (formattedDate: string) => {
          // Determine logo and contact info based on user role
          const logoUrl =
            isBuilder && profile?.logo_url
              ? profile.logo_url
              : "/images/logo-pdf.jpeg";
          const contactEmail =
            isBuilder && profile?.contact_email
              ? profile.contact_email
              : "info@lotwiseapp.com";
          const contactPhone =
            isBuilder && profile?.phone ? profile.phone : "(281) 595-0929";
          const contactWebsite =
            isBuilder && profile?.company_website
              ? profile.company_website
              : "lotwiseapp.com";

          try {
            // Optimize logo for header
            const logoResult = await optimizeImageForPDF(logoUrl, {
              maxWidth: 150,
              maxHeight: 150,
              quality: 0.8,
              format: "jpeg",
            });
            pdf.addImage(logoResult.dataURL, "JPEG", 20, 5, 18, 18);
          } catch {
            // Fallback to colored rectangle if logo fails
            pdf.setDrawColor(0, 100, 0);
            pdf.setFillColor(0, 100, 0);
            pdf.rect(20, 5, 18, 18, "F");
          }

          pdf.setFontSize(8);
          pdf.setTextColor(0);
          pdf.text(contactEmail, 40, 12);
          pdf.text(contactPhone, 40, 16);
          pdf.text(contactWebsite, 40, 20);

          pdf.setDrawColor(0);
          pdf.line(20, 23, 195, 23);

          pdf.setFontSize(8);
          pdf.text(formattedDate, 190, 20, { align: "right" });
        };

        const addWatermark = async () => {
          // Skip watermark for builders
          if (isBuilder) return;

          try {
            pdf.saveGraphicsState();
            pdf.setGState(
              new (
                pdf as unknown as {
                  GState: new (opts: { opacity: number }) => unknown;
                }
              ).GState({ opacity: 0.2 }) as unknown as string,
            );

            // Use smaller, optimized watermark
            const watermarkResult = await optimizeImageForPDF(
              "/images/logo-pdf.jpeg",
              {
                maxWidth: 500,
                maxHeight: 500,
                quality: 0.8, // Higher quality for watermark (0.8 = 80% quality)
                format: "jpeg",
              },
            );

            const pw = 210;
            const ph = 280;
            const logoSize = 200; // Increased watermark size
            const centerX = (pw - logoSize) / 2;
            const centerY = (ph - logoSize) / 2 + 20; // Move down to avoid header

            pdf.addImage(
              watermarkResult.dataURL,
              "JPEG",
              centerX,
              centerY,
              logoSize,
              logoSize,
            );
            pdf.restoreGraphicsState();
          } catch {
            // Skip watermark if optimization fails
            pdf.restoreGraphicsState();
          }
        };

        const addFooter = () => {
          const footerY = 265;
          pdf.line(20, footerY, 195, footerY);
          pdf.setFontSize(5);
          pdf.setFont("helvetica", "bold");
          // Add "POWERED BY LOTWISE | " prefix for builders
          const disclaimerTitle = isBuilder
            ? "POWERED BY LOTWISE | DESCRIPTIVE DISCLAIMER"
            : "DESCRIPTIVE DISCLAIMER";
          pdf.text(disclaimerTitle, 105, footerY + 3, {
            align: "center",
          });
          pdf.setFont("helvetica", "normal");
          const disclaimer =
            "This document has been prepared to assist the property owner in complying with city, county or HOA permitting requirements. It includes detailed site layout, structure specifications, and certified\nmeasurements provided based on client input and verified placement.\nNOTE: This document is for informational and compliance support purposes only. It does not substitute or override local permitting rules, building codes, or legal approvals.";
          pdf.text(disclaimer, 105, footerY + 6, {
            align: "center",
            maxWidth: 160,
          });
        };

        const formattedDate = new Date().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });

        await addWatermark();
        await addHeader(formattedDate);
        yPosition = 28;

        // Title
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.text(
          "Residential Accessory Structure",
          pageWidth / 2,
          yPosition + 4,
          {
            align: "center",
          },
        );
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.text("Placement & Overview", pageWidth / 2, yPosition + 8, {
          align: "center",
        });
        yPosition += 15;

        // Technical Description
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.text("Technical Description", 20, yPosition);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        const descText =
          "This document provides a visual and technical summary of the proposed accessory structure to be installed " +
          "on the referenced property. the placement has been marked directly onto the property survey provided by " +
          "the client, including approximate setbacks, property lines and relevant details.\n" +
          "It also includes a detailed list of materials, structure type, dimensions, anchoring method, and entry features " +
          "(doors, ramps, ventilation and windows). This summary is designed to support architectural review processes.";
        pdf.text(descText, 20, yPosition + 5, {
          maxWidth: 170,
          lineHeightFactor: 1.3,
        });
        yPosition += 27;

        pdf.setDrawColor(0);
        pdf.line(20, yPosition, 195, yPosition);
        yPosition += 6;

        const { dimensions, shedStyle, isAttached, hasBlocks } = shedConfig;

        const capitalizeWords = (str: string) => {
          if (!str || str === "N/A") return str;
          return str
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ");
        };

        const capitalizeFirst = (str: string) => {
          if (!str || str === "N/A") return str;
          return str.charAt(0).toUpperCase() + str.slice(1);
        };

        // Property details
        const basicInfo: [string, string][] = [
          ["Property Address:", form.address || "N/A"],
          ["Name:", capitalizeWords(form.name) || "N/A"],
          ["Mail:", form.email || "N/A"],
          ["Phone Number:", form.phone || "N/A"],
          ["Type:", shedStyle ? `Storage shed ${shedStyle} type` : "N/A"],
          [
            "Dimensions:",
            dimensions.width && dimensions.length && dimensions.totalHeight
              ? `${dimensions.width} (W) x ${dimensions.length} (L) x ${dimensions.totalHeight} (H)`
              : "N/A",
          ],
          [
            "Fence distance:",
            dimensions.fenceDistance ? `${dimensions.fenceDistance} ft` : "N/A",
          ],
          [
            "Wind (MPH):",
            form.hasWindSpeed && form.windSpeed
              ? `Yes - ${form.windSpeed} MPH`
              : "No",
          ],
          [
            "Blocks:",
            hasBlocks === true ? "Yes" : hasBlocks === false ? "No" : "N/A",
          ],
          ["Anchored:", form.isAnchored ? "Yes" : "No"],
          [
            "Attached:",
            isAttached === true ? "Yes" : isAttached === false ? "No" : "N/A",
          ],
          [
            "Wall ext. color:",
            capitalizeFirst(String(dimensions.wallExteriorColor)) || "N/A",
          ],
        ];

        pdf.setFontSize(9);
        basicInfo.forEach(([label, value]) => {
          pdf.setFont("helvetica", "bold");
          pdf.text(label, 20, yPosition);
          pdf.setFont("helvetica", "normal");
          pdf.text(value, 50, yPosition);
          yPosition += 4;
        });

        const floorDistanceInfo = dimensions.floorDistance
          ? `Height from ground to structure floor: ${dimensions.floorDistance} in`
          : "Height from ground to structure floor: Not specified";

        const addImageWithObjectFit = async (
          imagePath: string,
          x: number,
          y: number,
          containerSize: number,
        ) => {
          try {
            // Detect optimal format for the image
            const format = await detectOptimalFormat(imagePath);
            const preserveTransparency = format === "png";

            // Optimize image with smart compression
            const optimizedImage = await optimizeImageForPDF(imagePath, {
              maxWidth: 800, // Reduced max size for better compression
              maxHeight: 800,
              quality: 0.75, // Balanced quality/size ratio
              format,
              preserveTransparency,
            });

            console.log(`Image optimized: ${imagePath}`);
            console.log(
              `Size reduction: ${(((optimizedImage.originalSize - optimizedImage.optimizedSize) / optimizedImage.originalSize) * 100).toFixed(1)}%`,
            );

            // Calculate layout with proper aspect ratio
            const imageAspectRatio =
              optimizedImage.width / optimizedImage.height;
            let imageWidth: number,
              imageHeight: number,
              offsetX = 0,
              offsetY = 0;

            if (imageAspectRatio > 1) {
              imageWidth = containerSize;
              imageHeight = containerSize / imageAspectRatio;
              offsetY = (containerSize - imageHeight) / 2;
            } else {
              imageHeight = containerSize;
              imageWidth = containerSize * imageAspectRatio;
              offsetX = (containerSize - imageWidth) / 2;
            }

            // Add optimized image to PDF
            pdf.addImage(
              optimizedImage.dataURL,
              optimizedImage.format.toUpperCase() as "JPEG" | "PNG",
              x + offsetX,
              y + offsetY,
              imageWidth,
              imageHeight,
            );
          } catch (error) {
            console.warn(`Failed to optimize image ${imagePath}:`, error);
            // Fallback to original implementation
            await addImageFallback(imagePath, x, y, containerSize);
          }
        };

        // Fallback function for when optimization fails
        const addImageFallback = async (
          imagePath: string,
          x: number,
          y: number,
          containerSize: number,
        ) => {
          return new Promise<void>((resolve) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
              try {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d")!;

                // Use smaller scale for compression
                const scale = 0.3; // Reduced from 0.5
                canvas.width = Math.min(img.width * scale, 600);
                canvas.height = Math.min(img.height * scale, 600);

                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = "high";
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                const imgData = canvas.toDataURL("image/jpeg", 0.5); // Lower quality

                const imageAspectRatio = canvas.width / canvas.height;
                let imageWidth: number,
                  imageHeight: number,
                  offsetX = 0,
                  offsetY = 0;

                if (imageAspectRatio > 1) {
                  imageWidth = containerSize;
                  imageHeight = containerSize / imageAspectRatio;
                  offsetY = (containerSize - imageHeight) / 2;
                } else {
                  imageHeight = containerSize;
                  imageWidth = containerSize * imageAspectRatio;
                  offsetX = (containerSize - imageWidth) / 2;
                }

                pdf.addImage(
                  imgData,
                  "JPEG",
                  x + offsetX,
                  y + offsetY,
                  imageWidth,
                  imageHeight,
                );
                resolve();
              } catch {
                resolve();
              }
            };
            img.onerror = () => resolve();
            img.src = imagePath;
          });
        };

        const addMeasurementLines = (
          x: number,
          y: number,
          width: number,
          height: number,
          view: string,
        ) => {
          pdf.setFontSize(9);
          pdf.setTextColor(0);
          pdf.setFont("helvetica", "normal");
          if (view === "front") {
            pdf.text(`${dimensions.width}'`, x + width / 2, y + height - 10, {
              align: "center",
            });
            const totalHeight =
              dimensions.totalHeight || Number(dimensions.wallHeight) + 4;
            pdf.text(`${totalHeight}'`, x + width - 8, y + height / 2, {
              align: "center",
            });
          } else if (view === "side") {
            pdf.text(`${dimensions.length}'`, x + width / 2, y + height - 10, {
              align: "center",
            });
            pdf.text(
              `${dimensions.wallHeight}'`,
              x + width - 8,
              y + height / 2,
              { align: "center" },
            );
          }
        };

        // Shed images
        const imageStartY = yPosition - basicInfo.length * 4;
        const frontImageX = 105;
        const frontImageY = imageStartY;
        const squareSize = 90;
        const sideImageX = 105;
        const sideImageY = frontImageY + squareSize - 10;

        const shedImagePaths: Record<string, { front: string; side: string }> =
          {
            gable: {
              front: "/images/sheds/pdf-file/gable-front.jpeg",
              side: "/images/sheds/pdf-file/gable-side.jpeg",
            },
            barn: {
              front: "/images/sheds/pdf-file/barn-front.jpeg",
              side: "/images/sheds/pdf-file/barn-side.jpeg",
            },
            "lean-to": {
              front: "/images/sheds/pdf-file/lean-to-front.jpeg",
              side: "/images/sheds/pdf-file/lean-to-side.jpeg",
            },
          };

        const selectedImages =
          shedImagePaths[shedStyle] || shedImagePaths.gable;

        try {
          await addImageWithObjectFit(
            selectedImages.front,
            frontImageX,
            frontImageY,
            squareSize,
          );
          await addImageWithObjectFit(
            selectedImages.side,
            sideImageX,
            sideImageY,
            squareSize,
          );
          addMeasurementLines(
            frontImageX,
            frontImageY,
            squareSize,
            squareSize,
            "front",
          );
          addMeasurementLines(
            sideImageX,
            sideImageY,
            squareSize,
            squareSize,
            "side",
          );

          const disclaimerY = sideImageY + squareSize + 1;
          const disclaimerX = sideImageX + 15;
          pdf.setFontSize(5);
          pdf.setTextColor(80, 80, 80);
          pdf.setFont("helvetica", "normal");
          const disclaimerText =
            "Disclaimer: This 3D rendering is for illustrative purposes only; actual\nstructure may vary in features, appearance, and inclusion of vent and ramp.";
          pdf.text(disclaimerText, disclaimerX, disclaimerY, {
            maxWidth: squareSize,
            lineHeightFactor: 1.2,
          });
          pdf.setTextColor(0);

          const padding = -1;
          const frameX = frontImageX - padding + 1;
          const frameY = frontImageY - padding + 1;
          const frameWidth = squareSize + padding * 2;
          const disclaimerHeight = 10;
          const frameHeight =
            sideImageY +
            squareSize +
            disclaimerHeight +
            padding -
            frontImageY +
            padding * 2;
          pdf.setDrawColor(0, 0, 0);
          pdf.setLineWidth(0.2);
          pdf.setLineDashPattern([], 0);
          pdf.rect(frameX, frameY, frameWidth, frameHeight);
        } catch {
          // images are optional
        }

        let currentY = yPosition;

        pdf.setDrawColor(0);
        pdf.line(20, currentY, 100, currentY);
        currentY += 5;

        // Shed style structure
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.text(`${capitalizeFirst(shedStyle)} style structure`, 20, currentY);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        const dimensionText =
          dimensions.width &&
          dimensions.length &&
          dimensions.totalHeight &&
          dimensions.wallHeight
            ? `${dimensions.width} (Width) x ${dimensions.length} (Length) x ${dimensions.wallHeight} (Wall height) x ${dimensions.totalHeight} (Total Height)`
            : "Dimensions not specified";
        pdf.text(dimensionText, 20, currentY + 6);

        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.text(floorDistanceInfo, 20, currentY + 12);
        currentY += 15;

        // Components section
        currentY += 1;
        pdf.line(20, currentY, 100, currentY);
        currentY += 5;
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.text("COMPONENTS:", 20, currentY);

        const addRoundedBorder = (x: number, y: number, size: number) => {
          const radius = 2;
          pdf.setDrawColor(0);
          pdf.setLineWidth(0.3);
          pdf.roundedRect(x, y, size, size, radius, radius);
        };

        const iconSize = 13;
        const startX = 20;
        const iconSpacing = 2;
        const iconsYPos = currentY + 3;
        let iconX = startX;

        const selectedDoors = shedConfig.getSelectedDoors();
        const selectedExtras = shedConfig.getSelectedExtras();

        const addComponentIcon = async (
          imagePath: string,
          label: string,
          quantity: number,
          size: string | null = null,
        ) => {
          addRoundedBorder(iconX, iconsYPos, iconSize);
          try {
            // Detect optimal format and preserve transparency for PNG
            const format = await detectOptimalFormat(imagePath);
            const preserveTransparency = format === "png";

            const optimizedIcon = await optimizeImageForPDF(imagePath, {
              maxWidth: 128, // Slightly larger for better quality
              maxHeight: 128,
              quality: 0.75,
              format,
              preserveTransparency,
            });

            pdf.addImage(
              optimizedIcon.dataURL,
              optimizedIcon.format.toUpperCase() as "JPEG" | "PNG",
              iconX + 2,
              iconsYPos + 2,
              iconSize - 4,
              iconSize - 4,
            );
          } catch {
            // Fallback to gray rectangle if icon fails
            pdf.setFillColor(180, 180, 180);
            pdf.rect(iconX + 2, iconsYPos + 2, iconSize - 4, iconSize - 4, "F");
          }

          pdf.setFontSize(6);
          pdf.text(label, iconX + iconSize / 2, iconsYPos + iconSize + 3, {
            align: "center",
          });
          pdf.text(
            `(${quantity})`,
            iconX + iconSize / 2,
            iconsYPos + iconSize + 6,
            {
              align: "center",
            },
          );
          if (size) {
            pdf.text(size, iconX + iconSize / 2, iconsYPos + iconSize + 9, {
              align: "center",
            });
          }
          iconX += iconSize + iconSpacing;
        };

        const doorConfig: Record<string, { path: string; label: string }> = {
          rollUp: {
            path: "/images/doors/roll-up-front.png",
            label: "ROLL UP",
          },
          single: { path: "/images/doors/single-front.png", label: "SINGLE" },
          double: { path: "/images/doors/double-front.png", label: "DOUBLE" },
          house: { path: "/images/doors/house-front.png", label: "HOUSE" },
        };

        for (const door of selectedDoors) {
          const config = doorConfig[door.type];
          if (config) {
            const sizeText = door.size ? `${door.size}` : null;
            await addComponentIcon(
              config.path,
              config.label,
              door.quantity,
              sizeText,
            );
          }
        }

        const extraConfig: Record<string, { path: string; label: string }> = {
          windows: {
            path: "/images/extras/window-front.png",
            label: "WINDOW",
          },
          vents: { path: "/images/extras/vent-front.png", label: "VENT" },
        };

        for (const extra of selectedExtras) {
          const config = extraConfig[extra.type];
          if (config) {
            const sizeText = extra.size ? `${extra.size}` : null;
            await addComponentIcon(
              config.path,
              config.label,
              extra.quantity,
              sizeText,
            );
          }
        }

        // Materials section
        currentY += 25;
        pdf.line(20, currentY, 100, currentY); // Extended line for full review width
        currentY += 5;
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.text("MATERIALS:", 20, currentY);
        currentY += 3;

        const materialsText = form.materialsText || "No materials specified";
        const cleanedMaterials = materialsText
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
          .join("\n");
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        const maxWidth = 85; // Increased from 75 for better use of page width
        const maxLines = 15; // Increased to accommodate better wrapping
        const lineHeight = 3.2;
        const wrappedLines: string[] = pdf.splitTextToSize(
          cleanedMaterials,
          maxWidth,
        );
        let finalLines = wrappedLines;
        if (wrappedLines.length > maxLines) {
          finalLines = wrappedLines.slice(0, maxLines);
          if (finalLines[maxLines - 1]) {
            const lastLine = finalLines[maxLines - 1];
            finalLines[maxLines - 1] =
              lastLine.substring(0, Math.max(0, lastLine.length - 3)) + "...";
          }
        }

        finalLines.forEach((line, index) => {
          pdf.text(line, 20, currentY + 2 + index * lineHeight);
        });
        currentY += finalLines.length * lineHeight + 2;

        // Roof section
        currentY += -1;
        pdf.line(20, currentY, 100, currentY); // Extended line for consistency
        currentY += 4;
        pdf.setFont("helvetica", "bold");
        pdf.text("ROOF:", 20, currentY);
        const roofYPos = currentY + 2;

        let materialType = "No material specified";
        let roofColor = "No color specified";
        let roofBrand = "No brand specified";
        let roofType = "No type specified";
        let useCustomColorImage = false;

        if (form.materialType === "metal") {
          materialType = "METAL";
          roofType = "Metal";
          roofColor = form.metalColor || "Color not specified";
          useCustomColorImage = true;
        } else if (form.materialType === "shingles") {
          materialType = "SHINGLES";
          roofType = form.shingleType || "No type specified";
          const color =
            form.shingleColor === "other"
              ? form.shingleColorOther
              : form.shingleColor;
          roofColor = color || "No color specified";
          const brand =
            form.shingleBrand === "other"
              ? form.shingleBrandOther
              : form.shingleBrand;
          roofBrand = brand || "No brand specified";
          const otherColorUsed =
            form.shingleColor === "other" && form.shingleColorOther;
          useCustomColorImage = !!otherColorUsed;
        }

        let imagePath = "";
        if (useCustomColorImage) {
          imagePath = "/images/shingle-colors/custom-color.jpeg";
        } else if (materialType === "SHINGLES") {
          const predefinedColors = ["black", "brownwood", "driftwood", "gray"];
          const colorLower = roofColor.toLowerCase();
          if (predefinedColors.includes(colorLower)) {
            if (roofType.toLowerCase().includes("architectural")) {
              imagePath = `/images/shingle-colors/architectural/${colorLower}-architectural.jpeg`;
            } else if (
              roofType.toLowerCase().includes("3-tab") ||
              roofType.toLowerCase().includes("3tab")
            ) {
              imagePath = `/images/shingle-colors/3-tab/${colorLower}-3tab.jpeg`;
            }
          }
        }

        try {
          if (imagePath || materialType === "METAL") {
            const finalImagePath =
              imagePath || "/images/shingle-colors/custom-color.jpeg";

            // Optimize roofing material image
            try {
              const optimizedRoofImage = await optimizeImageForPDF(
                finalImagePath,
                {
                  maxWidth: 64,
                  maxHeight: 64,
                  quality: 0.6,
                  format: "jpeg",
                },
              );

              pdf.addImage(
                optimizedRoofImage.dataURL,
                "JPEG",
                22,
                roofYPos + 2,
                15,
                15,
              );
            } catch {
              // Skip roofing image if optimization fails
            }

            pdf.setFontSize(8);
            pdf.text(materialType, 29, roofYPos + 22, { align: "center" });
          }
        } catch {
          // optional
        }

        // Roof details
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.text("Type:", 45, roofYPos + 5);
        pdf.text("Color:", 45, roofYPos + 11);
        pdf.text("Brand:", 45, roofYPos + 17);
        pdf.setFont("helvetica", "normal");
        pdf.text(roofType, 57, roofYPos + 5);
        pdf.text(roofColor, 57, roofYPos + 11);
        if (roofBrand.length > 25) {
          const words = roofBrand.split(" ");
          if (words.length > 1) {
            const midPoint = Math.ceil(words.length / 2);
            const firstLine = words.slice(0, midPoint).join(" ");
            const secondLine = words.slice(midPoint).join(" ");
            pdf.text(firstLine, 57, roofYPos + 17);
            pdf.text(secondLine, 57, roofYPos + 21);
          } else {
            pdf.text(roofBrand, 57, roofYPos + 17);
          }
        } else {
          pdf.text(roofBrand, 57, roofYPos + 17);
        }

        addFooter();

        // Page 2: Survey visualization
        if (calibration.isSurveyLoaded && calibration.isShedLocked) {
          pdf.addPage();

          // Ensure header is added first for proper visibility
          await addHeader(formattedDate);
          await addWatermark();

          try {
            let sourceElement: HTMLImageElement | HTMLCanvasElement | null =
              null;
            if (
              calibration.surveyType === "image" &&
              calibration.imageViewerRef.current
            ) {
              sourceElement = calibration.imageViewerRef.current;
            } else if (
              calibration.surveyType === "pdf" &&
              calibration.pdfViewerRef.current
            ) {
              sourceElement = calibration.pdfViewerRef.current;
            }

            if (sourceElement) {
              const sourceWidth =
                (sourceElement as HTMLImageElement).naturalWidth ||
                sourceElement.width;
              const sourceHeight =
                (sourceElement as HTMLImageElement).naturalHeight ||
                sourceElement.height;

              const maxDimension = 1500;
              let scaledWidth = sourceWidth;
              let scaledHeight = sourceHeight;

              if (sourceWidth > maxDimension || sourceHeight > maxDimension) {
                const scale = Math.min(
                  maxDimension / sourceWidth,
                  maxDimension / sourceHeight,
                );
                scaledWidth = Math.floor(sourceWidth * scale);
                scaledHeight = Math.floor(sourceHeight * scale);
              }

              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d")!;
              canvas.width = scaledWidth;
              canvas.height = scaledHeight;

              ctx.fillStyle = "#ffffff";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(sourceElement, 0, 0, canvas.width, canvas.height);

              const canvasScale = scaledWidth / sourceWidth;

              // Calculate shed dimensions with minimum size for consistency (same as ShedPositioner)
              const calculateShedDimensions = () => {
                const baseWidthPx =
                  ((Number(dimensions.width) || 10) /
                    calibration.scaleFactor!) *
                  canvasScale;
                const baseLengthPx =
                  ((Number(dimensions.length) || 12) /
                    calibration.scaleFactor!) *
                  canvasScale;

                // For images, apply minimum size to ensure consistency
                if (
                  calibration.surveyType === "image" &&
                  sourceWidth &&
                  sourceHeight
                ) {
                  const scaleX = canvas.width / sourceWidth;
                  const scaleY = canvas.height / sourceHeight;

                  // Calculate display size with current scaling
                  const currentDisplayWidth =
                    (baseWidthPx / canvasScale) * scaleX;
                  const currentDisplayHeight =
                    (baseLengthPx / canvasScale) * scaleY;

                  // Responsive minimum display size (25px for desktop, 15px for mobile)
                  const isMobile =
                    typeof window !== "undefined" && window.innerWidth <= 768;
                  const minDisplaySize = isMobile ? 15 : 25;

                  // If the shed would be too small in display, adjust the base size
                  if (
                    currentDisplayWidth < minDisplaySize ||
                    currentDisplayHeight < minDisplaySize
                  ) {
                    const widthMultiplier =
                      currentDisplayWidth < minDisplaySize
                        ? minDisplaySize / currentDisplayWidth
                        : 1;
                    const heightMultiplier =
                      currentDisplayHeight < minDisplaySize
                        ? minDisplaySize / currentDisplayHeight
                        : 1;
                    const multiplier = Math.max(
                      widthMultiplier,
                      heightMultiplier,
                    );

                    return {
                      width: baseWidthPx * multiplier,
                      length: baseLengthPx * multiplier,
                    };
                  }
                }

                return {
                  width: baseWidthPx,
                  length: baseLengthPx,
                };
              };

              const { width: shedWidthPx, length: shedLengthPx } =
                calculateShedDimensions();
              const shedX = calibration.shedPosition.x * canvasScale;
              const shedY = calibration.shedPosition.y * canvasScale;

              ctx.save();
              ctx.translate(shedX, shedY);
              ctx.rotate((calibration.shedRotation * Math.PI) / 180);

              const textScale = Math.max(
                Math.min(canvas.width / 800, canvas.height / 600),
                0.8,
              );
              const lw = Math.max(4 * textScale, 3); // Thicker lines for better visibility

              ctx.strokeStyle = "#000000"; // Pure black for maximum contrast
              ctx.lineWidth = lw;
              ctx.setLineDash([]);
              ctx.strokeRect(
                -shedWidthPx / 2,
                -shedLengthPx / 2,
                shedWidthPx,
                shedLengthPx,
              );

              ctx.beginPath();
              ctx.moveTo(-shedWidthPx / 2, -shedLengthPx / 2);
              ctx.lineTo(shedWidthPx / 2, shedLengthPx / 2);
              ctx.moveTo(shedWidthPx / 2, -shedLengthPx / 2);
              ctx.lineTo(-shedWidthPx / 2, shedLengthPx / 2);
              ctx.stroke();

              const fontSize = Math.max(16 * textScale, 14); // Larger font size
              ctx.font = `bold ${fontSize}px Arial`;
              ctx.fillStyle = "#000000"; // Pure black text
              ctx.strokeStyle = "#ffffff"; // White outline for text
              ctx.lineWidth = Math.max(4 * textScale, 3); // Thicker text outline
              const textOffset = 20 * textScale;

              const widthText = `W:${dimensions.width || "10"}'`;
              const widthTextWidth = ctx.measureText(widthText).width;
              ctx.strokeText(
                widthText,
                -widthTextWidth / 2,
                -shedLengthPx / 2 - textOffset,
              );
              ctx.fillText(
                widthText,
                -widthTextWidth / 2,
                -shedLengthPx / 2 - textOffset,
              );

              const lengthText = `L:${dimensions.length || "12"}'`;
              ctx.strokeText(
                lengthText,
                shedWidthPx / 2 + textOffset,
                fontSize / 3,
              );
              ctx.fillText(
                lengthText,
                shedWidthPx / 2 + textOffset,
                fontSize / 3,
              );

              const heightText = `H:${dimensions.totalHeight || "8"}'`;
              const heightTextWidth = ctx.measureText(heightText).width;
              ctx.strokeText(
                heightText,
                -heightTextWidth / 2,
                shedLengthPx / 2 + textOffset + fontSize,
              );
              ctx.fillText(
                heightText,
                -heightTextWidth / 2,
                shedLengthPx / 2 + textOffset + fontSize,
              );

              if (dimensions.fenceDistance) {
                const distanceText = `D:${dimensions.fenceDistance}'`;
                const distanceTextWidth = ctx.measureText(distanceText).width;
                ctx.strokeText(
                  distanceText,
                  -shedWidthPx / 2 - textOffset - distanceTextWidth,
                  fontSize / 3,
                );
                ctx.fillText(
                  distanceText,
                  -shedWidthPx / 2 - textOffset - distanceTextWidth,
                  fontSize / 3,
                );
              }

              ctx.restore();

              const headerLineY = 23;
              const footerLineY = 265;
              const lineStartX = 20;
              const lineEndX = 195;
              const imgWidth = lineEndX - lineStartX;
              const imgHeight = footerLineY - headerLineY;

              // Optimize the large survey canvas for smaller file size
              try {
                // Create a smaller version of the canvas for better compression
                const outputCanvas = document.createElement("canvas");
                const outputCtx = outputCanvas.getContext("2d")!;

                // Calculate optimal dimensions (max 1000px width to reduce file size)
                const maxWidth = 1000;
                const scale = Math.min(maxWidth / canvas.width, 1);

                outputCanvas.width = canvas.width * scale;
                outputCanvas.height = canvas.height * scale;

                // Use high-quality scaling
                outputCtx.imageSmoothingEnabled = true;
                outputCtx.imageSmoothingQuality = "high";

                // Fill with white background (no transparency needed for survey)
                outputCtx.fillStyle = "#FFFFFF";
                outputCtx.fillRect(
                  0,
                  0,
                  outputCanvas.width,
                  outputCanvas.height,
                );

                // Draw the scaled survey
                outputCtx.drawImage(
                  canvas,
                  0,
                  0,
                  outputCanvas.width,
                  outputCanvas.height,
                );

                // Try JPEG first (much smaller than PNG for this type of image)
                const jpegData = outputCanvas.toDataURL("image/jpeg", 0.7);
                const pngData = outputCanvas.toDataURL("image/png");

                // Use JPEG if it's significantly smaller and no transparency is needed
                const useJPEG = jpegData.length < pngData.length * 0.7;
                const imgData = useJPEG ? jpegData : pngData;

                console.log(
                  `Survey image optimized: ${useJPEG ? "JPEG" : "PNG"} format selected`,
                );
                console.log(
                  `Original size estimate: ${((canvas.width * canvas.height * 4) / 1024 / 1024).toFixed(2)}MB`,
                );
                console.log(
                  `Compressed size estimate: ${((imgData.length * 0.75) / 1024 / 1024).toFixed(2)}MB`,
                );

                pdf.addImage(
                  imgData,
                  useJPEG ? "JPEG" : "PNG",
                  lineStartX,
                  headerLineY,
                  imgWidth,
                  imgHeight,
                );
              } catch (optimizationError) {
                console.warn(
                  "Survey image optimization failed, using basic compression:",
                  optimizationError,
                );
                // Fallback to simple PNG compression
                const imgData = canvas.toDataURL("image/png", 0.3);
                pdf.addImage(
                  imgData,
                  "PNG",
                  lineStartX,
                  headerLineY,
                  imgWidth,
                  imgHeight,
                );
              }
            }
          } catch {
            pdf.setFontSize(14);
            pdf.text(
              "Survey Map / Property Visualization",
              pageWidth / 2,
              120,
              { align: "center" },
            );
            pdf.setFontSize(10);
            pdf.text(
              "(Survey visualization could not be generated)",
              pageWidth / 2,
              135,
              {
                align: "center",
              },
            );
          }

          addFooter();
        }

        const fileName = `permit-document-lotwise.pdf`;

        try {
          const pdfBlob = pdf.output("blob");
          const pdfFile = new File([pdfBlob], fileName, {
            type: "application/pdf",
          });
          const presign = await getPresignedUrl(fileName, "application/pdf");
          await uploadFileToS3(presign.upload_url, pdfFile);
          const configuration = buildConfiguration(
            form,
            calibration,
            shedConfig,
          );
          await createSurvey({
            customer_name: form.name,
            customer_phone: form.phone,
            pdf_s3_key: presign.s3_key,
            configuration,
          });
          pdf.save(fileName);
          toast.success("Report saved successfully");
        } catch (error) {
          Sentry.captureException(error);
          console.error("Failed to save report to server:", error);
          pdf.save(fileName);
          toast.warning(
            "PDF downloaded locally, but failed to save to server.",
          );
        }

        return true; // PDF was downloaded successfully
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error generating PDF:", error);
        alert("Error generating PDF. Please try again.");
        return false;
      } finally {
        setIsGenerating(false);
      }
    },
    [],
  );

  return {
    generatePdf,
    isGenerating,
  };
}
